import React, { useContext, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import DispositionNode from "../../interfaces/dispositionNode";
import CollapsibleComponent from "./collapsibleComponents";
import FieldItem from "./FieldItem";
import Disposition from "../../interfaces/disposition";
import DispositionInput from "../../interfaces/disposition_input";
import DispositionContext from "../../context/dispositionContext";

interface DispositionTreeViewProps {
    dispositionNode: DispositionNode;
};

const DispositionTreeView: React.FC<DispositionTreeViewProps> = ({ dispositionNode }) => {
    const { openDispositionModal, openDispositionInputModal, openDeleteModalFunction, interactionMode } = useContext(DispositionContext);
    const [isChildrenVisible, setIsChildrenVisible] = useState<boolean>(false);

    const handleAddDisposition = (isDispositionInputsPresent: boolean, dispositionNode: DispositionNode) => {
        if (isDispositionInputsPresent) {
            alert('There are disposition inputs associated with this parent disposition. Please remove them before inserting a disposition as a child of this parent disposition');
        } else {
            if (openDispositionModal) {
                openDispositionModal(dispositionNode.node.disposition_id, undefined);
            }
        }
    };

    const handleAddDispositionInput = (isDispositionPresent: boolean, dispositionID: string) => {
        if (isDispositionPresent) {
            alert('There are dispositions associated with this parent disposition. Please remove them before inserting a disposition input as a child of this parent disposition');
        } else {
            if (openDispositionInputModal) {
                openDispositionInputModal(dispositionID, undefined);
            }
        }
    };

    const editDisposition = (dispositionNode: DispositionNode) => {
        if (openDispositionModal) {
            openDispositionModal(dispositionNode.node.parent_id, dispositionNode);
        }
    };

    const editDispositionInput = (dispositionInput: DispositionInput) => {
        if (openDispositionInputModal) {
            openDispositionInputModal(dispositionInput.disposition_id, dispositionInput);
        }
    };

    const deleteDisposition = (dispositionNodeVal: DispositionNode) => {
        if (dispositionNodeVal.children.filter(val => val.node.is_active).length > 0 || (dispositionNodeVal.dispositionInputs && dispositionNodeVal.dispositionInputs.filter(val => val.is_active).length > 0)) {
            alert('Please remove the descendants before deleting this disposition');
        } else if (openDeleteModalFunction) {
            openDeleteModalFunction(dispositionNodeVal, undefined);
        }
    };

    const deleteDispositionInput = (dispositionInput: DispositionInput) => {
        if (openDeleteModalFunction) {
            openDeleteModalFunction(undefined, dispositionInput);
        }
    };

    const DispositionDetailsView = ({ dispositionNodeVal }: { dispositionNodeVal: DispositionNode }) => {
        return (
            <Box>
                {dispositionNodeVal.node.is_standard ? null : (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button variant="outlined" onClick={() => editDisposition(dispositionNodeVal)}>
                            <Typography>Edit</Typography>
                        </Button>
                        <Button sx={{ ml: 2 }} variant="outlined" color="error" onClick={() => deleteDisposition(dispositionNodeVal)}>
                            <Typography>Delete</Typography>
                        </Button>
                    </Box>
                )}
                <FieldItem fieldName="Disposition Name" value={dispositionNodeVal.node.disposition_name} />
                <FieldItem fieldName="Disposition Label" value={dispositionNodeVal.node.disposition_label} />
                <FieldItem fieldName="Interaction Mode" value={dispositionNodeVal.node.interaction_mode} />
                {dispositionNodeVal.node.interaction_type && (
                    <FieldItem fieldName="Interaction Type" value={dispositionNodeVal.node.interaction_type} />
                )}
            </Box>
        );
    };

    const DispositionInputView = ({ dispositionInput, disposition }: { dispositionInput: DispositionInput, disposition: Disposition }) => {
        return (
            <Box>
                {disposition.is_standard ? null : (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button variant="outlined" onClick={() => editDispositionInput(dispositionInput)}>
                            <Typography>Edit</Typography>
                        </Button>
                        <Button sx={{ ml: 2 }} variant="outlined" color="error" onClick={() => deleteDispositionInput(dispositionInput)}>
                            <Typography>Delete</Typography>
                        </Button>
                    </Box>
                )}
                <FieldItem fieldName="Field Name" value={dispositionInput.field_name} />
                <FieldItem fieldName="Field Label" value={dispositionInput.label} />
                <FieldItem fieldName="Input Type" value={dispositionInput.input_type} />
                {dispositionInput.config && dispositionInput.config?.items && (
                    <FieldItem fieldName="Picklist Values" value={dispositionInput.config?.items} />
                )}
                {dispositionInput.config && dispositionInput.config?.min !== undefined && (
                    <FieldItem fieldName="Min Date Limit" value={dispositionInput.config?.min} />
                )}
                {dispositionInput.config && dispositionInput.config?.max !== undefined && (
                    <FieldItem fieldName="Max Date Limit" value={dispositionInput.config?.max} />
                )}
                <FieldItem fieldName="Mandatory Status" value={dispositionInput.is_mandatory ? 'True' : 'False'} />
            </Box>
        );
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CollapsibleComponent
                    style={{ width: '50%', mr: 1 }}
                    headerText={dispositionNode.node.disposition_name}
                    content={<DispositionDetailsView dispositionNodeVal={dispositionNode} />}
                />
                <IconButton onClick={() => setIsChildrenVisible(!isChildrenVisible)}>
                    {isChildrenVisible ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
                </IconButton>
            </Box>
            <Box sx={{ ml: 10 }}>
                {isChildrenVisible && (
                    <>
                        {dispositionNode.node.is_standard ? null : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    sx={{ mt: 1, mb: 1, mr: 1 }}
                                    onClick={() => handleAddDisposition(dispositionNode.dispositionInputs !== undefined && dispositionNode.dispositionInputs.filter(val => val.is_active).length > 0, dispositionNode)}
                                >
                                    <Typography variant="subtitle2">Add Disposition</Typography>
                                </Button>
                                <Button
                                    variant="outlined"
                                    sx={{ mt: 1, mb: 1 }}
                                    onClick={() => handleAddDispositionInput(dispositionNode.children.filter(val => val.node.is_active).length > 0, dispositionNode.node.disposition_id)}
                                >
                                    <Typography variant="subtitle2">Add Disposition Input</Typography>
                                </Button>
                            </Box>
                        )}
                    </>
                )}
                {isChildrenVisible ? dispositionNode.children.filter(val => 
                    interactionMode.every(value => val.node.interaction_mode.split(',').includes(value)) && val.node.is_active
                ).length > 0 ? (
                    <>
                        {dispositionNode.children.filter(
                            val => interactionMode.every(value => val.node.interaction_mode.split(',').includes(value)) && val.node.is_active
                        ).map((value: DispositionNode, index: number) => {
                            return (
                                <DispositionTreeView key={index} dispositionNode={value} />
                            );
                        })}
                    </>
                ) : dispositionNode.dispositionInputs && dispositionNode.dispositionInputs.filter(val => val.is_active).length > 0 ? (
                    <>
                        {dispositionNode.dispositionInputs.filter(val => val.is_active).map((value: DispositionInput, index: number) => {
                            return (
                                <CollapsibleComponent
                                    style={{ width: '50%' }}
                                    headerText={value.field_name}
                                    key={index}
                                    content={<DispositionInputView dispositionInput={value} disposition={dispositionNode.node} />}
                                />
                            );
                        })}
                    </>
                ) : null : null}
            </Box>
        </Box>
    );
};

export default DispositionTreeView;